// 工具函数

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'active':
    case 'paid':
      return 'text-green-600 bg-green-50';
    case 'canceled':
      return 'text-gray-600 bg-gray-50';
    case 'past_due':
    case 'failed':
      return 'text-red-600 bg-red-50';
    case 'pending':
      return 'text-amber-600 bg-amber-50';
    default:
      return 'text-gray-600 bg-gray-50';
  }
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// 根据浏览器语言获取对应的货币
export const getCurrency = (defaultLang: string): string => {
  const lang = (defaultLang || navigator.language).toLocaleLowerCase();

  if (lang === 'zh-cn') {
    return 'CNY';
  }
  if (lang === 'zh-tw' || lang.includes('tw') || lang.includes('hk') || lang.includes('zh-mo')) {
    return 'TWD';
  }
  if (lang === 'ja-jp' || lang.includes('ja')) {
    return 'JPY';
  }

  return 'USD';
};

// 根据货币单位格式化价格
export const formatPrice = (value: number, currency: string): string => {
  const currencyInfoMap: Record<string, { prefix?: string; suffix?: string }> = {
    CNY: { prefix: '¥ ' },
    JPY: { suffix: ' 円' },
    TWD: { suffix: ' NT$' },
    USD: { suffix: ' USD' },
  };

  const matchItem = currencyInfoMap[currency];
  const prefixStr = matchItem?.prefix || '';
  const suffixStr = matchItem?.suffix || '';

  if (matchItem) {
    return `${prefixStr}${Intl.NumberFormat().format(value)}${suffixStr}`;
  }

  return `${currency} ${value}`;
};

// 多语言文本处理 - 现在通过 react-i18next 统一处理
// 保留此函数以避免破坏现有代码，但建议直接使用 t() 函数
export const getLocalizedText = (
  lang: string,
  textKey: string,
  params?: Record<string, any>
): string => {
  // 此函数已废弃，建议使用 t() 函数直接获取翻译
  console.warn('getLocalizedText is deprecated. Please use t() function from react-i18next instead.');
  return '';
};

// 将真实产品数据转换为 AddonPackage 格式
export const convertProductsToAddonPackages = (
  products: API.ProductListResponseModified[],
  t: (key: string, options?: any) => string,
  currency: string = 'USD'
): import('./types').AddonPackage[] => {
  // Top-up 包名称映射
  const packageNames = ['Trial', 'Professional', 'Premium', 'Premium Plus'];
  
  console.log('products', products);
  // 取前4个产品作为Pro计划的Top-up包
  return products.filter(product => product.name?.includes('Purchase'))
  .map((product, index) => {
    const totalSeconds = product.product_item?.total || 0;
    const minutes = Math.ceil(totalSeconds / 60); // total是秒数，转换为分钟
    const targetPrice = product.price_list.find(p => p.currency === currency);
    const price = targetPrice?.price || 0;
    
    const hoursDecimal = (minutes / 60).toFixed(1);
    const localizedDescription = t('account.topupDescription', { hours: hoursDecimal }) || `Extra ${hoursDecimal} hours when monthly quota is insufficient`;
    
    return {
      id: product.product_id,
      name: packageNames[index] || `Package ${index + 1}`,
      hours: minutes.toString(), // 存储分钟数
      price: price,
      description: localizedDescription
    };
  });
};

// 将订阅产品转换为 Plan 格式
// summerDiscountMap: 夏季促销 product_id -> percent_off；命中档位时 price 换算成折后价并携带划线原价
export const convertSubscriptionProductsToPlans = (
  products: API.ProductListResponseModified[],
  t: (key: string, options?: any) => string,
  currency: string = 'USD',
  summerDiscountMap?: Record<string, number>,
  // 首月半价活动（与夏促时间互斥）：命中 productIds 的档位按 percentOff 折算首月价
  firstMonthPromo?: { percentOff: number; productIds: string[] }
): import('./types').Plan[] => {
  return products.map((product, index) => {
    const totalSeconds = product.product_item?.total || 0;
    const hours = totalSeconds > 0 ? (totalSeconds / 3600).toFixed(1) : 'Unlimited'; // 转换为小时
    const targetPrice = product.price_list.find(p => p.currency === currency);
    const price = targetPrice?.price || 0;

    // 促销折后价（与后端 coupon 共用同一 percent_off，展示与扣款一致；JPY/TWD 取整，其余两位小数）
    const summerPercentOff = price > 0 ? summerDiscountMap?.[product.product_id] : undefined;
    const firstMonthHit =
      price > 0 && !summerPercentOff && firstMonthPromo?.productIds.includes(product.product_id);
    const percentOff = summerPercentOff ?? (firstMonthHit ? firstMonthPromo!.percentOff : undefined);
    // TWD 不取整：奇数档折后 149.5，Stripe 实扣 NT$149.50，取整会致页面与账单金额不一致
    const discountedPrice = percentOff
      ? currency === 'JPY'
        ? Math.round((price * (100 - percentOff)) / 100)
        : Math.round(price * (100 - percentOff)) / 100
      : undefined;
    
    // 根据产品名称中是否包含"Business"来确定分类
    const category: 'personal' | 'business' = product.name?.includes('Business') ? 'business' : 'personal';
    
    // 基本功能列表
    const hoursFeature = hours === 'Unlimited' 
      ? (t('account.unlimitedHoursPerMonth') || 'Unlimited hours per month')
      : (t('account.hoursPerMonth', { hours }) || `${hours} hours per month`);
    
    const featuresIncluded = category === 'business' 
      ? (t('account.allBusinessFeatures') || 'All Business features included')
      : (t('account.allPersonalFeatures') || 'All Personal features included');
    
    const baseFeatures = [hoursFeature, featuresIncluded];
    
    // 商业版额外功能
    const businessFeatures = category === 'business' ? [
      t('account.teamCollaboration') || 'Team collaboration',
      t('account.prioritySupport') || 'Priority support',
      t('account.advancedAnalytics') || 'Advanced analytics'
    ] : [];
    
    return {
      id: product.product_id,
      name: product.name || `Plan ${index + 1}`,
      price: discountedPrice ?? price,
      hours: hours,
      features: [...baseFeatures, ...businessFeatures],
      category: category,
      popular: index === 1, // 将第二个产品设为推荐
      ...(discountedPrice !== undefined
        ? { originalPrice: price, discountPercent: percentOff, ...(firstMonthHit ? { firstMonth: true } : {}) }
        : {})
    };
  });
};